import React, { useEffect, useMemo } from 'react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { CompanyQueryData } from '@area-restrita/api-client/model/companyQueryData';
import { CompanyDocumentDetailQueryResponse } from '@area-restrita/api-client/model/companyDocumentDetailQueryResponse';
import {
  getGetCompanyDocumentByIdQueryKey,
  getGetCompanyDocumentRelatedCompaniesQueryKey,
  useUpdateCompanyDocumentPermission,
} from '@area-restrita/api-client/endpoints/company-document/company-document.gen';
import { DepartmentQueryData } from '@area-restrita/api-client/model/departmentQueryData';

import {
  Card,
  CardDescription,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import DataTable from '@/components/DataTable';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import { cn } from '@/lib/utils';

import { factoryColumns, PermissionTableData } from './columns';

const factoryData = (
  companies: CompanyQueryData[],
  departments: DepartmentQueryData[],
) =>
  companies.map(
    (company) =>
      ({
        id: company.id,
        tradingName: company.tradingName,
        ...Object.fromEntries(departments.map(({ id }) => [id, false])),
      }) as PermissionTableData,
  );

interface IProps {
  companyDocument: CompanyDocumentDetailQueryResponse;
  companies: CompanyQueryData[];
  departments: DepartmentQueryData[];
}

const CompanyDocumentPermission: React.FC<IProps> = ({
  companies,
  departments,
  companyDocument,
}) => {
  const { checkAuthorization } = useAuthSessionStore();
  const canUpdate = checkAuthorization(({ can }) =>
    can('update', 'document_permissions'),
  );
  const queryClient = useQueryClient();

  const form = useForm<{
    isPublic?: boolean;
    all?: boolean;
    company: Record<number, boolean | null>;
    department: Record<string, boolean>;
    granular: Record<string, Record<number, boolean | null>>;
  }>({
    defaultValues: {
      isPublic: companyDocument.isPublic,
      all: companyDocument.permissions.isGlobal,
      company: Object.fromEntries(
        companyDocument.permissions.companyIds.map((id) => [id, true]),
      ),
      department: Object.fromEntries(
        companyDocument.permissions.departmentIds.map((id) => [id, true]),
      ),
      granular: Object.fromEntries(
        companyDocument.permissions.granular.map(({ departmentId }) => [
          departmentId,
          Object.fromEntries(
            companyDocument.permissions.granular
              .filter(({ departmentId: dId }) => dId === departmentId)
              .map(({ companyId }) => [companyId, true]),
          ),
        ]),
      ),
    },
  });

  const { mutateAsync: updateCompanyDocumentPermission } =
    useUpdateCompanyDocumentPermission({
      mutation: {
        onSuccess: async () => {
          await Promise.all(
            [
              getGetCompanyDocumentByIdQueryKey(companyDocument.publicId),
              getGetCompanyDocumentRelatedCompaniesQueryKey(
                companyDocument.publicId,
              ),
            ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );
          toast.success('Permissões atualizadas com sucesso');
        },
        onError: () => {
          toast.error('Erro ao atualizar permissões');
        },
      },
    });

  const data = useMemo(
    () => factoryData(companies, departments),
    [companies, departments],
  );
  const columns = useMemo(() => factoryColumns(departments), [departments]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const subscription = form.watch((_, info) => {
      if (!info.name) return;

      if (info.name === 'isPublic' && info.type === 'change') {
        updateCompanyDocumentPermission({
          id: companyDocument.publicId,
          data: { isPublic: form.getValues(info.name) },
        });
      }

      if (info.name === 'all' && info.type === 'change') {
        updateCompanyDocumentPermission({
          id: companyDocument.publicId,
          data: { isGlobal: form.getValues(info.name) },
        });
      }

      if (info.name.startsWith('company.') && info.type === 'change') {
        updateCompanyDocumentPermission({
          id: companyDocument.publicId,
          data: {
            companyId: Number(info.name.replace('company.', '')),
            departmentId: null,
          },
        });
      }

      if (info.name.startsWith('department.') && info.type === 'change') {
        updateCompanyDocumentPermission({
          id: companyDocument.publicId,
          data: {
            companyId: null,
            departmentId: Number(info.name.replace('department.', '')),
          },
        });
      }

      if (info.name.startsWith('granular.') && info.type === 'change') {
        updateCompanyDocumentPermission({
          id: companyDocument.publicId,
          data: {
            companyId: Number(info.name.replace('granular.', '').split('.')[1]),
            departmentId: Number(
              info.name.replace('granular.', '').split('.')[0],
            ),
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, updateCompanyDocumentPermission, companyDocument.publicId]);

  return (
    <Form {...form}>
      <Card className="flex-1 h-full overflow-auto">
        <CardHeader>
          <CardTitle>Permissões</CardTitle>
          <CardDescription>
            Defina quem pode acessar este documento
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 max-h-[calc(100vh-21rem)] overflow-auto">
          <div className="p-6">
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Permissão Pública
                    </FormLabel>
                    <FormDescription>
                      Ative esta opção para permitir que o documento seja
                      acessado por qualquer pessoa, mesmo sem estar logada.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canUpdate}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <DataTable
            className={cn(
              'whitespace-nowrap',
              '[&>thead>tr>th:first-child]:sticky [&>thead>tr>th:first-child]:left-0',
              '[&>thead>tr>th:first-child]:bg-white [&>thead>tr>th:first-child]:pr-4',
              '[&>thead>tr>th:first-child]:z-[1]',
              '[&>thead>tr>th:last-child]:pr-4',
              '[&>tbody>tr>td:first-child]:sticky [&>tbody>tr>td:first-child]:left-0',
              '[&>tbody>tr>td:first-child]:bg-white [&>tbody>tr>td:first-child]:pr-4',
              '[&>tbody>tr>td:first-child]:z-[1]',
              '[&>tbody>tr>td:last-child]:pr-4',
            )}
            table={table}
          />
        </CardContent>
      </Card>
    </Form>
  );
};

export default CompanyDocumentPermission;
